import React from 'react';
import { Box, Container, Typography, Card, Grid, alpha, Button, Avatar, Chip } from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import { formatProduct } from '../../../utils/utilfunction';
import { useTheme } from '@mui/material/styles';

const PreferencesTab = ({
  userSettings,
  handleOpenSettingsDialog
}) => {
  const theme = useTheme();

  const EditButton = (
    <Button
      variant="contained"
      size="small"
      onClick={handleOpenSettingsDialog}
      sx={{
        py: 0.5,
        px: 2,
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 10,
        boxShadow: `0 4px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        fontSize: '0.8rem',
        '&:hover': {
          boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
        }
      }}
      startIcon={
        <Avatar sx={{ 
          width: 20, 
          height: 20, 
          backgroundColor: 'rgba(255,255,255,0.2)',
          mr: -0.5 
        }}>
          <Settings sx={{ fontSize: '0.8rem' }} />
        </Avatar>
      }
    >
      Edit Settings
    </Button>
  );

  return (
    <Box sx={{ 
      width: '100%',
      mb: 2,
      borderRadius: { xs: 2, sm: 3 },
      border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
      backgroundColor: theme.palette.background.paper,
      boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
      overflow: 'hidden'
    }}>

      <Box 
        sx={{ 
          px: { xs: 2, md: 2 }, 
          py: 1.5, 
          position: 'relative',
          overflow: 'hidden',
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(45deg, ${alpha(theme.palette.grey[900], 0.9)}, ${alpha(theme.palette.grey[800], 0.9)})`
            : `linear-gradient(45deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography 
            variant="subtitle1" 
            sx={{
              fontWeight: 700, 
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              position: 'relative',
              zIndex: 2,
            }}
          >

            Trading Settings
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              mt: 0, 
              maxWidth: 500,
              position: 'relative',
              zIndex: 2,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              fontWeight: 500
            }}
          >
            Manage your default settings
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          {EditButton}
        </Box>
      </Box>
        
        <Box sx={{ p: { xs: 1.5, sm: 2 }, backgroundColor: 'transparent' }}>
          <Grid container spacing={1.5} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: '100%',
                p: { xs: 1.5, sm: 2 }, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                transition: 'all 0.3s ease',
                position: 'relative',
                backdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0, fontSize: '0.95rem' }}>
                      Order Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      Default order type for new trades
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  mt: 'auto', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 1,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                  <Chip
                    label={userSettings?.order_type}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      '& .MuiChip-label': { px: 1.5 }
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: '100%',
                p: { xs: 1.5, sm: 2 }, 
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                transition: 'all 0.3s ease',
                position: 'relative',
                backdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5 }}>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0, fontSize: '0.95rem' }}>
                      Product Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      Default product type for trading
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  mt: 'auto', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 1,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                }}>
                  <Chip
                    label={formatProduct(userSettings?.product_type)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                      color: theme.palette.secondary.main,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                      '& .MuiChip-label': { px: 1.5 }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 1.5, 
            p: { xs: 1.5, sm: 2 }, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
            boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
          }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0, fontSize: '0.95rem' }}>
                      Default Quantities
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                      Exchange specific default order quantities
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['NSE', 'BSE', 'NFO', 'BFO', 'MCX', 'DCSX', 'CDS'].map((exchange) => {
                    const qty = userSettings?.default_qty_by_exchange?.[exchange] || 1;
                    return (
                      <Chip
                        key={exchange}
                        label={`${exchange}: ${qty}`}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                          backgroundColor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        }}
                      />
                    );
                  })}
                </Box>
            </Box>

          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'flex-end', mt: 2 }}>
            {EditButton}
          </Box>
        </Box>
    </Box>
  );
};

export default PreferencesTab; 